import React, { useState, useEffect } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import {
    FaTachometerAlt, FaUpload, FaHistory, FaBook, FaUsers,
    FaRulerCombined, FaTable, FaCogs, FaCodeBranch, FaFileAlt,
    FaFileInvoice, FaCalculator, FaWrench, FaExclamationTriangle,
    FaMoon, FaSun, FaClipboardList, FaRobot, FaIndustry, FaChartPie, FaChartLine, FaChartBar, FaBriefcaseMedical,
    FaBuilding, FaChevronDown, FaChevronRight, FaCalendarAlt, FaPlus, FaTimes
} from 'react-icons/fa';
import { useCompetence } from '../contexts/CompetenceContext';

const SidebarItem = ({ to, icon, label, onClick }) => {
    return (
        <li>
            <NavLink to={to} className={({ isActive }) => isActive ? "active-link" : ""} onClick={onClick}>
                {icon} {label}
            </NavLink>
        </li>
    );
};

const SidebarSubMenu = ({ title, icon, children, isOpen, onToggle }) => {
    return (
        <li className={`submenu - container ${isOpen ? 'open' : ''} `}>
            <div className="submenu-header" onClick={onToggle}>
                <span className="submenu-title">
                    {icon && <span className="submenu-icon">{icon}</span>}
                    {title}
                </span>
                <span className="submenu-chevron">
                    {isOpen ? <FaChevronDown size={12} /> : <FaChevronRight size={12} />}
                </span>
            </div>
            {isOpen && (
                <ul className="submenu-items">
                    {children}
                </ul>
            )}
        </li>
    );
};

const Layout = ({ children, isDarkMode, toggleTheme }) => {
    const location = useLocation();
    const { globalCompetence, setGlobalCompetence, availableMonths } = useCompetence();

    const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(() => {
        const savedState = localStorage.getItem('sidebar-collapsed');
        return savedState === 'true';
    });

    // Read permissions from localStorage
    const [permissions, setPermissions] = useState(() => {
        try {
            return JSON.parse(localStorage.getItem('permissions') || '{}');
        } catch {
            return {};
        }
    });

    const role = localStorage.getItem('role') || 'user';

    // State for collapsible menus
    const [openMenus, setOpenMenus] = useState({
        overview: true,
        masterData: false,
        inputs: false,
        logic: false,
        reports: false,
        admin: false
    });

    useEffect(() => {
        localStorage.setItem('sidebar-collapsed', isSidebarCollapsed);
    }, [isSidebarCollapsed]);

    // Auto-expand menu based on current route
    useEffect(() => {
        const path = location.pathname;
        if (['/dashboard', '/historico'].some(p => path.startsWith(p))) {
            setOpenMenus(prev => ({ ...prev, overview: true }));
        } else if (['/orcamento', '/centros-de-custo', '/linhas-servico', '/config', '/unidades-producao', '/tipos-alocacao', '/tabelas-auxiliares', '/rateio-por-item', '/alocacao-custos'].some(p => path.startsWith(p))) {
            setOpenMenus(prev => ({ ...prev, masterData: true }));
        } else if (['/competencias', '/processamento', '/producao', '/insumos'].some(p => path.startsWith(p))) {
            setOpenMenus(prev => ({ ...prev, inputs: true }));
        } else if (['/relatorio-conferencia', '/relatorio-custo-unitario', '/demonstrativos',
            '/monitoramento',
            '/analise-orcamentaria', '/tabela-dinamica'
        ].includes(location.pathname)) {
            setOpenMenus(prev => ({ ...prev, reports: true }));
        } else if (['/companies', '/admin'].some(p => path.startsWith(p))) {
            setOpenMenus(prev => ({ ...prev, admin: true }));
        }
    }, [location.pathname]);

    const toggleSidebar = () => {
        setIsSidebarCollapsed(!isSidebarCollapsed);
    };

    const toggleMenu = (menu) => {
        setOpenMenus(prev => ({ ...prev, [menu]: !prev[menu] }));
    };

    return (
        <div className="app-container">
            <aside className={`sidebar ${isSidebarCollapsed ? 'collapsed' : ''} glassmorphism`}>
                <div className="sidebar-header">
                    <h1>SACH <span style={{ fontWeight: 300 }}>PRO</span></h1>
                    <button className="sidebar-toggle" onClick={toggleSidebar} title={isSidebarCollapsed ? "Expandir menu" : "Recolher menu"}>
                        {isSidebarCollapsed ? '☰' : '✕'}
                    </button>
                </div>

                <div className="theme-toggle">
                    <div style={{ display: 'flex', alignItems: 'center', fontSize: '0.85rem', color: 'var(--text-muted)' }}>
                        {isDarkMode ? <FaMoon style={{ marginRight: 8, color: '#fbbf24' }} /> : <FaSun style={{ marginRight: 8, color: '#f59e0b' }} />}
                        <span>{isDarkMode ? 'Dark' : 'Light'}</span>
                    </div>
                    <label className="switch">
                        <input type="checkbox" checked={isDarkMode} onChange={toggleTheme} />
                        <span className="slider"></span>
                    </label>
                </div>

                {/* Global Competence Selector */}
                {/* Global Competence Selector */}
                <div className="global-competence-selector glassmorphism" style={{
                    padding: '12px 15px',
                    margin: '10px',
                    borderRadius: '12px',
                    border: '1px solid var(--border-color)',
                    background: 'rgba(255,255,255,0.4)'
                }}>
                    <div style={{ display: 'flex', alignItems: 'center', marginBottom: 8 }}>
                        <div style={{ display: 'flex', alignItems: 'center', fontSize: '0.75rem', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                            <FaCalendarAlt style={{ marginRight: 6, color: 'var(--primary-color)' }} />
                            Competência Global
                        </div>
                    </div>

                    <select
                        value={globalCompetence}
                        onChange={(e) => setGlobalCompetence(e.target.value)}
                        style={{
                            width: '100%',
                            padding: '8px 10px',
                            borderRadius: '6px',
                            border: '1px solid var(--border-color)',
                            backgroundColor: 'var(--card-bg)',
                            color: 'var(--text-color)',
                            fontSize: '0.9rem',
                            fontWeight: 500,
                            cursor: 'pointer'
                        }}
                    >
                        {(!availableMonths || availableMonths.length === 0) && <option value="">Selecione...</option>}
                        {(availableMonths || []).map(month => (
                            <option key={month} value={month}>{month}</option>
                        ))}
                    </select>
                </div>

                <nav className="sidebar-nav">
                    <ul>
                        {/* 1. VISÃO GERAL */}
                        <SidebarSubMenu
                            title="Visão Geral"
                            icon={<FaTachometerAlt />}
                            isOpen={openMenus.overview}
                            onToggle={() => toggleMenu('overview')}
                        >
                            <SidebarItem to="/dashboard" icon={<FaChartLine />} label="Painel" />
                        </SidebarSubMenu>

                        {/* 3. MOVIMENTAÇÃO MENSAL */}
                        {permissions.inputs && (
                            <SidebarSubMenu
                                title="Movimentação Mensal"
                                icon={<FaUpload />}
                                isOpen={openMenus.inputs}
                                onToggle={() => toggleMenu('inputs')}
                            >
                                <SidebarItem to="/competencias" icon={<FaCalendarAlt />} label="Competências" />
                                <SidebarItem to="/processamento" icon={<FaUpload />} label="Balancete" />
                                <SidebarItem to="/producao" icon={<FaIndustry />} label="Produção hospitalar" />
                                <SidebarItem to="/insumos" icon={<FaTable />} label="Tabelas de Insumo" />
                                <SidebarItem to="/custo-padrao" icon={<FaCalculator />} label="Custo Padrão" />
                            </SidebarSubMenu>
                        )}

                        {/* 2. CADASTROS BASE */}
                        {permissions.settings && (
                            <SidebarSubMenu
                                title="Cadastros Base"
                                icon={<FaBook />}
                                isOpen={openMenus.masterData}
                                onToggle={() => toggleMenu('masterData')}
                            >
                                <SidebarItem to="/orcamento" icon={<FaCalculator />} label="Controle Orçamentário" />
                                <SidebarItem to="/centros-de-custo" icon={<FaUsers />} label="Centros de Custo" />
                                <SidebarItem to="/linhas-servico" icon={<FaChartPie />} label="Linhas de Serviço" />
                                <SidebarItem to="/config" icon={<FaClipboardList />} label="Plano de Contas" />
                                <SidebarItem to="/unidades-producao" icon={<FaRulerCombined />} label="Unidades de Produção" />
                                <SidebarItem to="/tipos-alocacao" icon={<FaCogs />} label="Tipos de Alocação" />
                                <SidebarItem to="/alocacao-custos" icon={<FaCodeBranch />} label="Regras de Rateio" />
                                <SidebarItem to="/tabelas-auxiliares" icon={<FaTable />} label="Tabelas Auxiliares" />
                                <SidebarItem to="/rateio-por-item" icon={<FaClipboardList />} label="Rateio por Item" />
                            </SidebarSubMenu>
                        )}

                        {/* 3. IMPORTAÇÃO E INSUMOS */}
                        <SidebarSubMenu
                            title="Movimentação Mensal"
                            icon={<FaUpload />}
                            isOpen={openMenus.inputs}
                            onToggle={() => toggleMenu('inputs')}
                        >
                            <SidebarItem to="/competencias" icon={<FaCalendarAlt />} label="Competências" />
                            <SidebarItem to="/processamento" icon={<FaUpload />} label="Balancete" />
                            <SidebarItem to="/producao" icon={<FaIndustry />} label="Produção hospitalar" />
                            <SidebarItem to="/insumos" icon={<FaTable />} label="Tabelas de Insumo" />
                            <SidebarItem to="/custo-padrao" icon={<FaCalculator />} label="Custo Padrão" />
                        </SidebarSubMenu>

                        {/* 4. RELATÓRIOS E RESULTADOS */}
                        <SidebarSubMenu
                            title="Relatórios e Resultados"
                            icon={<FaFileAlt />}
                            isOpen={openMenus.reports}
                            onToggle={() => toggleMenu('reports')}
                        >
                            <SidebarItem to="/relatorio-conferencia" icon={<FaFileInvoice />} label="Conferência" />
                            <SidebarItem to="/relatorio-custo-unitario" icon={<FaCalculator />} label="Custo Unitário" />
                            <SidebarItem to="/analise-orcamentaria" icon={<FaChartBar />} label="Análise Orçamentária" />
                            <SidebarItem to="/analise-custos-avancada" icon={<FaBriefcaseMedical />} label="Análise Avançada de Custos" />
                            <SidebarItem to="/demonstrativos" icon={<FaChartLine />} label="Demonstrativos" />
                            <SidebarItem to="/analise-producao" icon={<FaChartPie />} label="Análise Produção" />
                            <SidebarItem to="/lucratividade" icon={<FaChartLine />} label="Lucratividade" />
                            <SidebarItem to="/ai-analysis" icon={<FaRobot />} label="Análise IA" />
                            <SidebarItem to="/monitoramento" icon={<FaExclamationTriangle />} label="Monitoramento" />
                            <SidebarItem to="/tabela-dinamica" icon={<FaTable />} label="Tabela Dinâmica" />
                        </SidebarSubMenu>

                        {/* 6. ADMINISTRAÇÃO */}
                        {role === 'admin' && (
                            <SidebarSubMenu
                                title="Administração"
                                icon={<FaWrench />}
                                isOpen={openMenus.admin}
                                onToggle={() => toggleMenu('admin')}
                            >
                                <SidebarItem to="/companies" icon={<FaBuilding />} label="Empresas" />
                                <SidebarItem to="/admin" icon={<FaUsers />} label="Gerenciar Usuários" />
                                <SidebarItem to="/documentacao" icon={<FaBook />} label="Documentação Corporativa" />
                            </SidebarSubMenu>
                        )}
                    </ul>
                </nav>

                <div className="user-info">
                    <div className="user-details">
                        <p style={{ fontWeight: 600, marginBottom: '0.25rem' }}>
                            {localStorage.getItem('role') === 'admin' ? '👑 Admin' : '👤 Usuário'}
                            <span style={{ fontSize: '0.6em', color: 'red' }}> ({localStorage.getItem('role')})</span>
                        </p>
                        <p style={{ fontSize: '0.85rem', opacity: 0.7, marginBottom: '0.5rem' }}>{localStorage.getItem('username') || 'admin'}</p>

                        {localStorage.getItem('company_name') && (
                            <div style={{ marginTop: '0.5rem', borderTop: '1px solid rgba(255,255,255,0.1)', paddingTop: '0.5rem' }}>
                                <p style={{ fontSize: '0.7rem', opacity: 0.6, marginBottom: '0' }}>Nome do Projeto:</p>
                                <p style={{ fontSize: '0.75rem', color: 'var(--primary-color)', fontWeight: 500, marginBottom: '0.3rem' }}>
                                    {localStorage.getItem('company_name')}
                                </p>

                                <p style={{ fontSize: '0.7rem', opacity: 0.6, marginBottom: '0' }}>Código:</p>
                                <p style={{ fontSize: '0.75rem', fontWeight: 500 }}>
                                    {localStorage.getItem('company_id')}
                                </p>
                            </div>
                        )}
                    </div>
                    <button
                        onClick={() => {
                            localStorage.clear();
                            window.location.href = '/';
                        }}
                        className="logout-button"
                        title="Sair do sistema"
                    >
                        <span>🚪</span> Sair
                    </button>
                </div>
            </aside >

            <main className={`main - content ${isSidebarCollapsed ? 'sidebar-collapsed' : ''} `}>
                {children}
            </main>
        </div >
    );
};

export default Layout;
